import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { DialogSheetComponent, IMPORT_DIALOG_HOST, dialogSheetHostClass } from '@lite-workspace/shared/helm-ui/dialog';

export type IntakeCheckFailedDecision = 'retry' | 'as-new';

export interface IntakeCheckFailedDialogContext {
  /** false = no upload-as-new escape (the wizard lane fails closed) — only retry or cancel. */
  readonly allowAsNew: boolean;
}

/** The fail-closed stop when the name check never answered: nothing uploads while it is open,
 *  and closing without a choice uploads nothing. */
@Component({
  selector: 'app-intake-check-failed-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmButton, DialogSheetComponent],
  // Окно мастера несёт контракт импорта; окно готового проекта остаётся прежним.
  host: { '[class]': 'hostClass' },
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <h2 [class]="titleClass">Не смогли свериться с проектом</h2>
        <p [class]="bodyClass">
          Проверка имён файлов не прошла. Без неё не поймём, что заменить, а что добавить — загрузка не началась.
        </p>
        <div [class]="footClass">
          @if (wizardChrome) {
            <button hlmBtn type="button" variant="outline" (click)="cancel()">Отмена</button>
            <button hlmBtn type="button" variant="default" (click)="close('retry')">Повторить проверку</button>
          } @else {
            <button hlmBtn type="button" variant="default" (click)="close('retry')">Повторить проверку</button>
            @if (allowAsNew) {
              <button hlmBtn type="button" variant="outline" (click)="close('as-new')">Загрузить как новые фото</button>
            }
            <button hlmBtn type="button" variant="ghost" (click)="cancel()">Отмена</button>
          }
        </div>
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class IntakeCheckFailedDialogComponent {
  private readonly ctx = injectBrnDialogContext<IntakeCheckFailedDialogContext | undefined>({ optional: true });
  private readonly dialogRef = inject(BrnDialogRef);

  protected readonly allowAsNew = this.ctx?.allowAsNew !== false;

  /** `allowAsNew: false` ставит только мастер (fail closed) — готовый проект контекста не передаёт вовсе. */
  protected readonly wizardChrome = this.ctx?.allowAsNew === false;
  protected readonly hostClass = this.wizardChrome
    ? IMPORT_DIALOG_HOST + ' dlg-desktop:w-[var(--dlg-w-s)]'
    : dialogSheetHostClass('s', 'p-6');

  protected readonly titleClass = this.wizardChrome ? 'mb-5 text-lg font-bold text-ink' : 'mb-1 text-lg font-bold text-ink';
  protected readonly bodyClass = this.wizardChrome
    ? 'mb-6 text-base leading-relaxed text-ink'
    : 'mb-5 text-base leading-relaxed text-ink';

  protected readonly footClass = this.wizardChrome ? 'flex justify-end gap-2' : 'flex flex-col gap-2';

  protected close(decision: IntakeCheckFailedDecision): void {
    this.dialogRef.close(decision);
  }

  protected cancel(): void {
    this.dialogRef.close(undefined);
  }
}
